import {
  Launch as LaunchIcon,
  MessageOutlined as MessageOutlinedIcon,
  Send as SendIcon,
  AutoAwesome as AISuggestionIcon, // Added for AI suggestion button
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Card,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmojiPicker from 'src/components/EmojiPicker';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import TextIconLabel from 'src/components/TextIconLabel';
import HttpServices from 'src/services/httpService';
import { postsModuleURL, userModuleURL } from 'src/services/urlService'; // Added userModuleURL
import { CommonHelper } from 'src/utils/commonHelper';
import Spinner from 'src/utils/Loader/spinner';
import ThreeDotLoader from 'src/utils/Loader/threeDotLoader.compontent';
import { useToaster } from 'src/utils/toaster/toasterContext';
import DeleteCommentIcon from '../../../assets/deletecomment.svg';
import FacebookIcon from '../../../assets/facebook.svg';
import FBlikeIcon from '../../../assets/fblike.svg';
import FacebookLikeIcon from '../../../assets/icons/facebook-reaction-like.svg';
import FacebookPostCard from '../publishedPostsPostCard/facebookPostCard.component';
import FacebookReelsCard from '../publishedPostsPostCard/facebookReelsCard.component';
import FacebookStoryCard from '../publishedPostsPostCard/facebookStoryCard.component';
import { useResellerLimitDialog } from 'src/components/reseller-limit-context/resellerLimitContext';

const FacebookPostDetailView = ({ pageId, postId, profile, height, viewType }) => {
  const { t: translate } = useTranslation();
  const sideBarWidth = 800;
  const hasManageLikesCommentPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.MANAGE_COMMENTS_LIKES
  );

  const fileInputRef = useRef(null);
  const commentInputRef = useRef(null);
  const childCommentInputRef = useRef(null);
  const [postDetails, setPostDetails] = useState(null);
  const [commentData, setCommentData] = useState(null);
  const [selectedComments, setSelectedComments] = useState([]);
  const [comment, setComment] = useState('');
  const [childComment, setChildComment] = useState('');
  const [childCommentId, setChildCommentId] = useState('');
  const { showToast } = useToaster();
  const [showLoader, setLoader] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState('');
  const [loaderComments, setLoaderComments] = useState(false);
  const [loaderReplies, setLoaderReplies] = useState(false);
  const { showResellerLimitDialog } = useResellerLimitDialog();
  const theme = useTheme();
  const fbColor = theme.palette.preview.facebook.color;
  const fbComment = theme.palette.preview.comments.facebook;

  // New state for AI suggestions
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggestionAnchorEl, setAiSuggestionAnchorEl] = useState(null);
  const [aiSuggestionCommentId, setAiSuggestionCommentId] = useState('');
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);

  const setCommentToInitialState = () => {
    setComment('');
    setChildComment('');
    setAiSuggestions([]); // Clear AI suggestions
    setAiSuggestionAnchorEl(null); // Close suggestion menu
    setAiSuggestionCommentId('');
  };

  useEffect(() => {
    getPostDetails();
  }, []);

  const facebookPostDetailsSuccessCallback = (data, message) => {
    let pDetails = {
      user_name: data.username,
      id: data.id,
      image_url: data.image_url,
      media_urls: data.media_urls,
      media_type: data.media_type,
      message: data.message,
      like_count: data.like_count,
      comment_count: data.comment_count,
      share_count: data.share_count,
      created_time: data.created_time,
      is_liked: data.is_liked,
      link: data.link,
    };
    setPostDetails(pDetails);
    setCommentData(data.comments_data);
    setPagination(data.comments_data.pagination);
    setLoader(false);
    setLoading(false);
    showToast(message, 's');
  };

  const facebookNextPostSuccessCallback = (data, message) => {
    let updatedComments = [...commentData.comments, ...data.comments_data.comments];
    let pagination = data.comments_data.pagination;
    setCommentData({
      comments: updatedComments,
      pagination: pagination,
    });
    setPagination(data.comments_data.pagination);
    setLoaderComments(false);
    showToast(message, 's');
  };

  const facebookNextReplySuccessCallback = (data, message, commentId) => {
    let updatedComments = commentData.comments.map((comment) => {
      if (comment.id === commentId) {
        return {
          ...comment,
          nested_comments: [...comment.nested_comments, ...data.comments_data.comments],
          pagination: data.comments_data.pagination,
        };
      }
      return comment;
    });
    setCommentData({
      comments: updatedComments,
      pagination: commentData.pagination,
    });
    setLoaderReplies(false);
  };

  const facebookPostDetailsFailureCallback = (message) => {
    setPostDetails(null);
    setCommentData(null);
    showToast(message, 'e');
    setLoader(false);
    setLoading(false);
    setLoaderComments(false);
    setLoaderReplies(false);
  };

  const getPostDetails = () => {
    setLoader(true);
    let baseUrl;
    switch (viewType) {
      case 'reels':
        baseUrl = postsModuleURL.getFacebookReelsDetails;
        break;
      case 'stories':
        baseUrl = postsModuleURL.getFacebookStoryDetails;
        break;
      default:
        baseUrl = postsModuleURL.getFacebookPostDetails;
        break;
    }

    let getProps = {
      url: `${baseUrl}${pageId}&post_id=${postId}`,
      successCallback: facebookPostDetailsSuccessCallback,
      failureCallback: facebookPostDetailsFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const getNextComments = () => {
    setLoaderComments(true);
    let getProps = {
      url: `${postsModuleURL.getFacebookNextComments}${pageId}&post_id=${postId}&after=${pagination}`,
      successCallback: facebookNextPostSuccessCallback,
      failureCallback: facebookPostDetailsFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const getNextReplies = (comment) => {
    setLoaderReplies(true);
    const commentId = comment.id;
    let getProps = {
      url: `${postsModuleURL.getFacebookNextReply}${pageId}&post_id=${commentId}&after=${comment.pagination}`,
      successCallback: (data, message) => facebookNextReplySuccessCallback(data, message, commentId),
      failureCallback: facebookPostDetailsFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  // New function to fetch AI suggestions
  const handleFetchAiSuggestions = (comment, event) => {
    if (!hasManageLikesCommentPermission) return;
    setIsFetchingSuggestions(true);
    setAiSuggestionCommentId(comment.id);
    setAiSuggestionAnchorEl(event.currentTarget);

    const bodyProps = {
      question: comment.text, // Use comment text as the question
    };

    const postProps = {
      url: userModuleURL.getAiSuggestionMessage,
      body: bodyProps,
      successCallback: (data, message) => {
        setAiSuggestions(data.suggestions || []);
        setIsFetchingSuggestions(false);
      },
      failureCallback: (message) => {
        setAiSuggestions([]);
        setIsFetchingSuggestions(false);
        setAiSuggestionAnchorEl(null);
        if (message && !showResellerLimitDialog(message)) {
          showToast(message, 'e');
        }
      },
    };

    HttpServices.Post(postProps);
  };

  // New function to handle selecting an AI suggestion
  const handleSelectAiSuggestion = (suggestion) => {
    setChildComment(suggestion);
    setChildCommentId(aiSuggestionCommentId);
    setAiSuggestionAnchorEl(null);
    setAiSuggestions([]);
    childCommentInputRef.current?.focus();
  };

  const handleChangeComment = (e, isParent) => {
    if (isParent) setComment(e.target.value);
    else setChildComment(e.target.value);
  };

  const handleClickAttach = () => {
    fileInputRef.current?.click();
  };

  const handleFocusComment = () => {
    commentInputRef.current?.focus();
  };

  const handleFocusChildComment = () => {
    childCommentInputRef.current?.focus();
  };

  const handleLikePost = (post) => {
    let bodyProps = {
      id: postDetails.id,
      connected_profile_id: pageId,
    };
    let postProps = {
      url: postsModuleURL.likeFacebookPost,
      body: bodyProps,
      failureCallback,
    };
    postDetails.is_liked = true;
    postDetails.like_count = postDetails.like_count + 1;
    setPostDetails({ ...postDetails });
    HttpServices.Post(postProps);
  };

  const handleUnLikePost = () => {
    if (viewType === 'reels') {
      showToast(translate('published_post.cannot_unlike_post_on_reels'), 'e');
      return;
    }
    let bodyProps = {
      id: postDetails.id,
      connected_profile_id: pageId,
    };
    let postProps = {
      url: postsModuleURL.unlikeFacebookPost,
      body: bodyProps,
      failureCallback,
    };
    postDetails.is_liked = false;
    if (postDetails.like_count > 0) postDetails.like_count = postDetails.like_count - 1;
    setPostDetails({ ...postDetails });
    HttpServices.Post(postProps);
  };

  const failureCallback = (message) => {
    postDetails.is_liked = !postDetails.is_liked;
    if (postDetails.is_liked) postDetails.like_count = postDetails.like_count + 1;
    if (postDetails.like_count > 0 && !postDetails.is_liked) postDetails.like_count = postDetails.like_count - 1;
    setPostDetails({ ...postDetails });
    message && showToast(message, 'e');
  };

  const postContent = () => {
    if (postDetails) {
      let cardDetails = {
        media_url: postDetails.image_url,
        media_urls: postDetails.media_urls,
        media_type: postDetails?.media_type,
        message: postDetails.message,
        like_count: postDetails.like_count,
        comment_count: postDetails.comment_count,
        share_count: postDetails.share_count,
        is_liked: postDetails.is_liked,
        profile,
        handleLikePost,
        handleUnLikePost,
        handleFocusComment,
      };
      if (viewType === 'reels') {
        return <FacebookReelsCard {...cardDetails} />;
      } else if (viewType === 'stories') {
        return <FacebookStoryCard {...cardDetails} />;
      } else {
        return <FacebookPostCard {...cardDetails} />;
      }
    } else return null;
  };

  const postComments = () => {
    return (
      <Stack spacing={1.5} sx={{ mt: 5, mb: 15 }}>
        {commentData &&
          commentData.comments &&
          commentData.comments.length > 0 &&
          commentData.comments.map((comment) => {
            return (
              <>
                <Card sx={{ m: '10px !important', marginRight: '12rem !important', boxShadow: 'none' }}>
                  {commentView(comment)}
                </Card>
                {selectedComments.some((a) => a.id === comment.id) &&
                  comment.nested_comments &&
                  comment.nested_comments.length > 0 && (
                    <>
                      {comment.nested_comments.map((nestedComment) => (
                        <Card
                          key={nestedComment.id}
                          sx={{ marginLeft: '80px !important', marginRight: '12rem !important', boxShadow: 'none' }}
                        >
                          {commentView(nestedComment, comment)}
                        </Card>
                      ))}
                      {comment.pagination !== '' && (
                        <Box sx={{ textAlign: 'center', marginRight: '12rem !important' }}>
                          {loaderReplies ? (
                            <ThreeDotLoader />
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 700,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                color: theme.palette.grey[500],
                              }}
                              onClick={() => getNextReplies(comment)}
                            >
                              {translate('published_post.next_replies')}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                {childCommentId && comment.id === childCommentId && writeContentView(childCommentId, childComment)}
              </>
            );
          })}
        {pagination && commentData.comments.length > 9 && (
          <Box sx={{ textAlign: 'center', marginRight: '12rem !important' }}>
            {loaderComments ? (
              <ThreeDotLoader />
            ) : (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: theme.palette.grey[500],
                }}
                onClick={getNextComments}
              >
                {translate('published_post.load_more_comments')}
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    );
  };

  const handleClickComment = (comment, parentComment) => {
    if (parentComment) {
      setChildCommentId(parentComment.id);
    } else {
      if (comment.comment_count === 0) {
        setChildCommentId(comment.id);
      } else {
        if (selectedComments.find((c) => c.id === comment.id)) {
          setChildCommentId(comment.id);
        }
        setSelectedComments([comment]);
        handleFocusComment();
      }
    }
  };

  const facebookLikeFailureCallback = (data, message, selectedComment, parentComment) => {
    if (message) {
      showToast(message, 'e');
    }
    if (parentComment) {
      let parentData = commentData.comments.find((a) => a.id === parentComment.id);
      let childComment = parentData.nested_comments.find((a) => a.id === selectedComment.id);
      if (childComment) {
        childComment.is_liked = false;
        if (childComment.like_count > 0) childComment.like_count = childComment.like_count - 1;
      }
    } else {
      let pComment = commentData.comments.find((a) => a.id === selectedComment.id);
      if (pComment) {
        pComment.is_liked = false;
        if (pComment.like_count > 0) pComment.like_count = pComment.like_count - 1;
      }
    }
    setCommentData({ ...commentData });
  };

  const facebookUnlikeFailureCallback = (message, selectedComment, parentComment) => {
    if (message) {
      showToast(message, 'e');
    }
    if (parentComment) {
      let parentData = commentData.comments.find((a) => a.id === parentComment.id);
      let childComment = parentData.nested_comments.find((a) => a.id === selectedComment.id);
      if (childComment) {
        childComment.is_liked = true;
        childComment.like_count = childComment.like_count + 1;
      }
    } else {
      let pComment = commentData.comments.find((a) => a.id === selectedComment.id);
      if (pComment) {
        pComment.is_liked = true;
        pComment.like_count = pComment.like_count + 1;
      }
    }
    setCommentData({ ...commentData });
  };

  const handleLikeComment = (selectedComment, parentComment) => {
    let bodyProps = {
      id: selectedComment.id,
      connected_profile_id: pageId,
    };
    let postProps = {
      url: postsModuleURL.likeFacebookPost,
      body: bodyProps,
      failureCallback: (data, message) => facebookLikeFailureCallback(data, message, selectedComment, parentComment),
    };
    if (parentComment) {
      let parentData = commentData.comments.find((a) => a.id === parentComment.id);
      let childComment = parentData.nested_comments.find((a) => a.id === selectedComment.id);
      if (childComment) {
        childComment.is_liked = true;
        childComment.like_count = childComment.like_count + 1;
      }
    } else {
      let pComment = commentData.comments.find((a) => a.id === selectedComment.id);
      if (pComment) {
        pComment.is_liked = true;
        pComment.like_count = pComment.like_count + 1;
      }
    }
    setCommentData({ ...commentData });
    HttpServices.Post(postProps);
  };

  const handleUnLikeComment = (selectedComment, parentComment) => {
    let bodyProps = {
      id: selectedComment.id,
      connected_profile_id: pageId,
    };
    let postProps = {
      url: postsModuleURL.unlikeFacebookPost,
      body: bodyProps,
      failureCallback: (message) => facebookUnlikeFailureCallback(message, selectedComment, parentComment),
    };
    if (parentComment) {
      let parentData = commentData.comments.find((a) => a.id === parentComment.id);
      let childComment = parentData.nested_comments.find((a) => a.id === selectedComment.id);
      if (childComment) {
        childComment.is_liked = false;
        if (childComment.like_count > 0) childComment.like_count = childComment.like_count - 1;
      }
    } else {
      let pComment = commentData.comments.find((a) => a.id === selectedComment.id);
      if (pComment) {
        pComment.is_liked = false;
        if (pComment.like_count > 0) pComment.like_count = pComment.like_count - 1;
      }
    }
    setCommentData({ ...commentData });
    HttpServices.Post(postProps);
  };

  const commentView = (comment, parentComment) => {
    return (
      <>
        <Stack
          key={comment.id}
          direction="row"
          spacing={2}
          style={{ marginLeft: 15, marginRight: 15, marginTop: 2, marginBottom: 2 }}
        >
          {parentComment && <Divider orientation="vertical" flexItem sx={{ borderLeftWidth: 2 }} />}
          <Avatar
            alt={comment.username}
            src={comment.profile_url}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              height: 40,
              width: 40,
              bgcolor: (theme) => theme.palette.primary.main,
            }}
          />
          <Paper sx={{ p: 1.5, flexGrow: 1, bgcolor: 'background.neutral', boxShadow: 3, borderRadius: '10px' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Typography variant="subtitle2">{comment.username}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {comment.text}
            </Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {CommonHelper.DateTimeFromCurrentTime(comment.created_time)}
              </Typography>
              <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{
                  color: 'text.disabled',
                }}
              >
                <StyledIconButton
                  onClick={() => {
                    hasManageLikesCommentPermission &&
                      (comment.is_liked
                        ? handleUnLikeComment(comment, parentComment)
                        : handleLikeComment(comment, parentComment));
                  }}
                >
                  <TextIconLabel
                    aria-label="chatbubbleoutline"
                    value={comment.like_count ? comment.like_count : ''}
                    sx={{ typography: 'subtitle2' }}
                    icon={
                      comment.is_liked ? (
                        <img src={FacebookLikeIcon} style={{ width: 18, height: 18 }} />
                      ) : (
                        <img src={FBlikeIcon} style={{ width: 18, height: 18 }} />
                      )
                    }
                  />
                </StyledIconButton>
                <StyledIconButton
                  onClick={() => {
                    handleClickComment(comment, parentComment);
                  }}
                >
                  <TextIconLabel
                    aria-label="chatbubbleoutline"
                    value={comment.comment_count ? comment.comment_count : ''}
                    sx={{ typography: 'subtitle2' }}
                    icon={
                      <MessageOutlinedIcon
                        sx={{
                          width: 20,
                          height: 20,
                          color: comment.comment_count ? fbComment : 'theme.pallete.GREY[500]',
                        }}
                      />
                    }
                  />
                </StyledIconButton>
                {/* New AI Suggestion Button */}
                <StyledIconButton
                  onClick={(e) => handleFetchAiSuggestions(comment, e)}
                  disabled={!hasManageLikesCommentPermission || isFetchingSuggestions}
                >
                  <TextIconLabel
                    aria-label="ai-suggestion"
                    sx={{ typography: 'subtitle2' }}
                    icon={
                      <AISuggestionIcon
                        sx={{
                          width: 20,
                          height: 20,
                          color: isFetchingSuggestions ? theme.palette.grey[400] : theme.palette.primary.main,
                        }}
                      />
                    }
                  />
                </StyledIconButton>
                <StyledIconButton
                  disabled={!hasManageLikesCommentPermission}
                  onClick={() => {
                    handleDeleteComment(comment, parentComment);
                  }}
                >
                  <TextIconLabel
                    aria-label="chatbubbleoutline"
                    sx={{ typography: 'subtitle2' }}
                    icon={<img src={DeleteCommentIcon} style={{ width: 20, height: 20 }} />}
                  />
                </StyledIconButton>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
        {/* AI Suggestions Menu */}
        <Menu
          anchorEl={aiSuggestionAnchorEl}
          open={Boolean(aiSuggestionAnchorEl) && aiSuggestionCommentId === comment.id}
          onClose={() => {
            setAiSuggestionAnchorEl(null);
            setAiSuggestions([]);
            setAiSuggestionCommentId('');
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {isFetchingSuggestions ? (
            <ListItem
              disableGutters
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                px: 2,
                py: 2.5, // Increased vertical padding
                backgroundColor: theme.palette.background.paper, // Subtle background
                borderRadius: 1, // Soft rounded corners
                boxShadow: '0px 2px 8px rgba(0,0,0,0.05)', // Subtle elevation
              }}
            >
              <Typography
                variant="body1" // Slightly larger text
                sx={{
                  fontWeight: 600, // Increased weight
                  color: theme.palette.primary.main,
                  letterSpacing: 0.5, // Improved readability
                  mb: 0.5, // Space below text
                }}
              >
               {translate('ai_message_comments.generating_replies')}
              </Typography>
              <ThreeDotLoader
                sx={{
                  color: theme.palette.primary.light, // Lighter color for loader
                  fontSize: '1.5rem', // Larger loader
                  animation: 'pulse 1.5s infinite', // Add subtle pulsing
                }}
              />
            </ListItem>
          ) : aiSuggestions.length > 0 ? (
            <List sx={{ maxWidth: 300 }}>
              {aiSuggestions.map((suggestion, index) => (
                <ListItemButton
                  key={index}
                  onClick={() => handleSelectAiSuggestion(suggestion)}
                  sx={{
                    borderBottom: index < aiSuggestions.length - 1 ? `1px solid ${theme.palette.grey[300]}` : 'none',
                  }}
                >
                  <ListItemText
                    primary={suggestion}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { whiteSpace: 'normal', wordBreak: 'break-word' },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <ListItem>
              <ListItemText primary={translate('ai_message_comments.no_ai_suggestions')} />
            </ListItem>
          )}
        </Menu>
      </>
    );
  };

  const handleDeleteComment = (comment, parentComment) => {
    let deleteBody = {
      id: comment.id,
      connected_profile_id: pageId,
    };
    let postProps = {
      url: postsModuleURL.deleteFacebookCommentAndMessage,
      body: deleteBody,
      failureCallback: (message) => facebookDeleteFailureCallback(message, comment, parentComment),
    };
    if (parentComment) {
      let parentData = commentData.comments.find((a) => a.id === parentComment.id);
      let index = parentData.nested_comments.findIndex((a) => a.id === comment.id);
      parentData.nested_comments.splice(index, 1);
      parentData.comment_count -= 1;
    } else {
      let index = commentData.comments.findIndex((a) => a.id === comment.id);
      commentData.comments.splice(index, 1);
      setPostDetails((prevDetails) => ({
        ...prevDetails,
        comment_count: prevDetails.comment_count - 1,
      }));
    }
    setCommentData({ ...commentData });
    HttpServices.Post(postProps);
  };

  const handleEmoji = (emoji) => {
    setComment(comment + emoji);
  };

  const facebookDeleteSuccessCallback = (data, message) => {
    showToast(message, 's');
  };

  const facebookDeleteFailureCallback = (message, deletedComment, parentComment) => {
    message && showToast(message, 'e');
    if (parentComment) {
      let parentData = commentData.comments.find((a) => a.id === parentComment.id);
      parentData.nested_comments.push(deletedComment);
      parentData.comment_count += 1;
    } else {
      commentData.comments.push(deletedComment);
      setPostDetails((prevDetails) => ({
        ...prevDetails,
        comment_count: prevDetails.comment_count + 1,
      }));
    }
    setCommentData({ ...commentData });
  };

  const writeContentView = (id, comment, isParent = false) => {
    return (
      <Card
        sx={{
          ...(!isParent && { boxShadow: 'none' }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          style={{
            ...(isParent && {
              position: 'fixed',
              bottom: '0px',
              padding: '25px',
              width: sideBarWidth,
              background: 'white',
            }),
          }}
          sx={{ marginRight: '12rem !important', marginLeft: isParent ? '0px' : '80px !important' }}
        >
          <Avatar
            src={profile.profile_image ? profile.profile_image : profile.profile_name || profile.id}
            alt={profile.profile_name ? profile.profile_name : profile.id}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              height: 40,
              width: 40,
              bgcolor: (theme) => theme.palette.primary.main,
            }}
            style={{ color: 'white' }}
          />
          <TextField
            fullWidth
            size="small"
            value={comment}
            onKeyUp={(e) => {
              handleKeyUp(e, comment, isParent);
            }}
            inputRef={isParent ? commentInputRef : childCommentInputRef}
            placeholder={translate('reviews_questions.write_a_comment')}
            disabled={!hasManageLikesCommentPermission}
            onChange={(e) => {
              handleChangeComment(e, isParent);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isParent ? <EmojiPicker handleEmoji={handleEmoji} /> : <></>}
                </InputAdornment>
              ),
            }}
            sx={{
              ml: 2,
              mr: 1,
              '& fieldset': {
                borderWidth: `1px !important`,
                borderColor: (theme) => `${theme.palette.grey[500_32]} !important`,
              },
              opacity: !hasManageLikesCommentPermission ? 0.5 : 1,
            }}
          />
          <LoadingButton
            variant="outlined"
            loading={isLoading}
            endIcon={<SendIcon />}
            size=""
            onClick={() => {
              handlePostComment(comment, isParent);
            }}
            disabled={!comment || !hasManageLikesCommentPermission}
            sx={{
              border: 'none',
              '&.Mui-disabled': {
                border: 'none',
                opacity: 0.6,
              },
              '&.MuiLoadingButton-root:hover': {
                border: 'none',
                background: 'none',
              },
            }}
          ></LoadingButton>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} />
        </Stack>
      </Card>
    );
  };

  const handleKeyUp = (event, comment, isParent) => {
    if (event.key === 'Enter' && comment !== '' && hasManageLikesCommentPermission) {
      handlePostComment(comment, isParent);
    }
  };

  const handlePostComment = (comment, isParent) => {
    if (comment.length > 10000) {
      showToast(`${translate('published_post.character_limit')} ${comment.length}`, 'e');
      return;
    }
    setLoading(true);
    let postCommentBody = {
      connected_profile_id: pageId,
      id: isParent ? postId : childCommentId,
      message: comment,
    };

    let postProps = {
      url: postsModuleURL.addFacebookComment,
      body: postCommentBody,
      successCallback: (data, message) => getAfterAddCommentPostDetails(data, message, isParent, comment),
      failureCallback: (message) => facebookPostFailureCallback(message),
    };

    setCommentData({ ...commentData });
    HttpServices.Post(postProps);
  };

  const getAfterAddCommentPostDetails = (data, message, isParent, text) => {
    if (isParent) {
      commentData.comments.push({
        id: data.id,
        text: text,
        is_liked: false,
        like_count: 0,
        comment_count: 0,
        created_time: new Date().toISOString(),
        profile_url: profile?.profile_image,
        username: profile?.profile_name,
        nested_comments: [],
      });
      setPostDetails((prevDetails) => ({
        ...prevDetails,
        comment_count: prevDetails.comment_count + 1,
      }));
    } else {
      let parentComment = commentData.comments.find((c) => c.id === childCommentId);
      if (parentComment) {
        parentComment.nested_comments.push({
          id: data.id,
          text: text,
          is_liked: false,
          created_time: new Date().toISOString(),
          profile_url: profile?.profile_image,
          username: profile?.profile_name,
          like_count: 0,
        });
        parentComment.comment_count += 1;
      }
    }
    setCommentData({ ...commentData });
    setLoading(false);
    showToast(message, 's');
    setCommentToInitialState();
  };

  const facebookPostFailureCallback = (message) => {
    setLoading(false);
    message && showToast(message, 'e');
  };

  return (
    <>
      <StyledComponent.StyledStack sx={{ width: sideBarWidth }}>
        <Box sx={{ position: 'fixed', width: sideBarWidth, zIndex: 1000, background: 'white' }}>
          <Grid alignItems="center" display="flex" sx={{ pt: 1, pb: 1 }}>
            <img alt="Facebook_Icon" width={75} height={40} sx={{ fontSize: 20 }} src={FacebookIcon} />
            <Typography color={fbColor} style={{ fontSize: 25, fontWeight: 800 }}>
              Facebook
            </Typography>
            <a href={postDetails?.link} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto' }}>
              <LaunchIcon sx={{ fontSize: '2rem', mr: 2, color: 'primary.main' }} />
            </a>
          </Grid>
          <Divider />
        </Box>
        <Box sx={{ mt: '5.6rem' }}>
          {showLoader ? (
            <Spinner height={height} />
          ) : (
            <>
              <Grid display="flex" alignItems="flex-start" justifyContent="flex-start" sx={{ ml: '1.5rem !important' }}>
                {postContent()}
              </Grid>
              {(viewType === 'post' || viewType === 'reels') && (
                <>
                  {postComments()}
                  {writeContentView(pageId, comment, true)}
                </>
              )}
            </>
          )}
        </Box>
      </StyledComponent.StyledStack>
    </>
  );
};

// Styled Components
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    [theme.breakpoints.up('sm')]: {
      fontSize: '30px',
    },
    [theme.breakpoints.only('xs')]: {
      fontSize: '24px',
    },
  },
}));

export default FacebookPostDetailView;
